import { IconButton } from "@material-ui/core";
import { observer } from "mobx-react";
import * as React from "react";
import { ENABLE_MOVE_FROM_EVIDENCE_TO_RECEIVED } from "../../../config";
import { THEME } from "../../../theme/ThemeStore";
import { evidenceDateRangeOptions, getJobCategoryOptions, Option } from "../../../constants/index";
import { t } from "../../../i18n/util";
import { hrStore } from "../../../stores/HrStore";
import { useTableStore } from "../../../stores/TableStore";
import { DateRangeEnum, JobCategoryEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useScrollBlock } from "../../hooks/useScrollBlock";
import { useTableSelection } from "../../hooks/useTableSelection";
import { ContentWrapper } from "../../ui/ContentWrapper";
import {
    HrBotBackButton,
    HrBotButton,
    HrBotGreyButtonSimple,
    HrBotGreyButtonToggles,
    HrBotOrangeButton
} from "../../ui/Primitives";
import { EvidenceList } from "../EvidenceList";
import { BurgerMenu, MobileMenuElementContainer } from "../HrNavBarElements";
import { HrRoutes } from "../router/HrRoutes";

export const EvidenceSite = observer(() => {
    const isMobile = useMobileMediaQuery();

    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [openMoveDialog, setOpenMoveDialog] = React.useState(false);
    const [openMobileFilterMenu, setOpenMobileFilterMenu] = React.useState(false);

    // enables and disables delete and move buttons
    const [someSelected, setSomeSelected] = React.useState(false);

    const [blockScroll, allowScroll] = useScrollBlock();

    // blocks scroll when menu is open
    React.useEffect(() => {
        if (isMobile && openMobileFilterMenu) {
            blockScroll();
        } else {
            allowScroll();
        }
    }, [allowScroll, blockScroll, isMobile, openMobileFilterMenu]);

    // saves where to go on "back" from details view
    React.useEffect(() => {
        hrStore.backTarget = HrRoutes.EVIDENCE;
    }, []);

    const tableStore = useTableStore("evidence", {
        orderBy: "createdAt",
        orderDir: "asc"
    });

    const selectJobCategory = useTableSelection(
        getJobCategoryOptions().map((jobCategoryOption) => ({ id: jobCategoryOption.value })),
        hrStore.selectedJobCategoryIds.length > 0 ? hrStore.selectedJobCategoryIds : "all"
    );

    React.useEffect(() => {
        // save filter to store in order to persist it
        hrStore.selectedJobCategoryIds = selectJobCategory.selectedIds;
    }, [selectJobCategory.selectedIds]);

    const memoizedJobCategories = React.useMemo(
        () => (!selectJobCategory.allSelected ? (selectJobCategory.selectedIds as JobCategoryEnum[]) : []),
        [selectJobCategory.allSelected, selectJobCategory.selectedIds]
    );

    const handleCloseJobCategory = () => {
        selectJobCategory.selectedIds.length === 0 && selectJobCategory.toggleSelectAll();
    };

    const handleSelectDateRange = (dateRange: Option<DateRangeEnum>) => {
        hrStore.evidenceLastChangeSince = dateRange;
    };

    const handleResetFilters = () => {
        hrStore.evidenceLastChangeSince = evidenceDateRangeOptions[1];
        !selectJobCategory.allSelected && selectJobCategory.toggleSelectAll();
    };

    //#region dialog handling
    const handleOpenMoveDialog = () => {
        setOpenMoveDialog(true);
    };

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
    };
    //#endregion

    const tableHeader = isMobile ? (
        <>
            <div
                style={{
                    display: "flex",
                    paddingBottom: 16,
                    flexDirection: "column",
                    gap: 16
                }}
            >
                <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                    <tableStore.SearchField placeholder={t("screen.evidence.search")} />
                    <IconButton onClick={() => setOpenMobileFilterMenu(true)}>
                        <img src={THEME.Images.icFilter} alt="" />
                    </IconButton>
                </div>
                <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                    {ENABLE_MOVE_FROM_EVIDENCE_TO_RECEIVED && (
                        <HrBotOrangeButton
                            label={t("screen.evidence.moveToKanban")}
                            onClick={handleOpenMoveDialog}
                            disabled={!someSelected}
                            style={{ width: "100%" }}
                        />
                    )}
                    <IconButton disabled={!someSelected} onClick={handleOpenDeleteDialog}>
                        <img src={THEME.Images.icDelete} alt="" style={{ opacity: !someSelected ? 0.25 : 1 }} />
                    </IconButton>
                </div>
            </div>
            <BurgerMenu open={openMobileFilterMenu} style={{ zIndex: 11, paddingTop: 0 }}>
                <MobileMenuElementContainer>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "0 16px"
                        }}
                    >
                        <div style={{ width: 24 }} />
                        <h3>{t("screen.evidence.filterOptions")}</h3>
                        <img
                            src={THEME.Images.icClose}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setOpenMobileFilterMenu(false);
                            }}
                        />
                    </div>
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotGreyButtonToggles
                        sorted
                        descriptionText={t("screen.dashboard.division")}
                        onClose={handleCloseJobCategory}
                        options={getJobCategoryOptions()}
                        select={selectJobCategory}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotGreyButtonSimple
                        value={hrStore.evidenceLastChangeSince}
                        descriptionText={t("screen.evidence.show")}
                        options={evidenceDateRangeOptions}
                        onSelect={handleSelectDateRange}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotButton
                        label={t("screen.evidence.saveFilterOptions")}
                        type="button"
                        style={{ width: "fit-content" }}
                        onClick={() => setOpenMobileFilterMenu(false)}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotBackButton
                        label={t("screen.evidence.resetFilterOptions")}
                        style={{ width: "fit-content", padding: "18px 26px" }}
                        onClick={() => {
                            setOpenMobileFilterMenu(false);
                            handleResetFilters();
                        }}
                    />
                </MobileMenuElementContainer>
            </BurgerMenu>
        </>
    ) : (
        <div
            style={{
                display: "flex",
                paddingBottom: 36,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 32
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 36
                }}
            >
                <HrBotGreyButtonToggles
                    sorted
                    descriptionText={t("screen.dashboard.division")}
                    onClose={handleCloseJobCategory}
                    options={getJobCategoryOptions()}
                    select={selectJobCategory}
                />
                <HrBotGreyButtonSimple
                    value={hrStore.evidenceLastChangeSince}
                    descriptionText={t("screen.evidence.show")}
                    options={evidenceDateRangeOptions}
                    onSelect={handleSelectDateRange}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 16,
                    flexGrow: 1
                }}
            >
                {ENABLE_MOVE_FROM_EVIDENCE_TO_RECEIVED && (
                    <HrBotOrangeButton
                        label={t("screen.evidence.moveToKanban")}
                        onClick={handleOpenMoveDialog}
                        disabled={!someSelected}
                    />
                )}
                <IconButton disabled={!someSelected} onClick={handleOpenDeleteDialog} style={{ padding: 0 }}>
                    <img
                        src={THEME.Images.icDelete}
                        alt="delete"
                        style={{ opacity: !someSelected ? 0.25 : 1, width: 24 }}
                    />
                </IconButton>
                <tableStore.SearchField placeholder={t("screen.evidence.search")} maxWidth />
            </div>
        </div>
    );

    return (
        <div
            style={{ backgroundColor: THEME.customColors.backgroundLight, width: "100%", flexGrow: 1, display: "flex" }}
        >
            <ContentWrapper style={{ padding: isMobile ? 20 : "36px 56px 0", maxWidth: 1600, flexGrow: 1 }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexGrow: 1,
                        justifyContent: "space-between",
                        marginBottom: 50,
                        gap: 32
                    }}
                >
                    <div style={{ width: "100%" }}>
                        {tableHeader}
                        <EvidenceList
                            jobCategories={memoizedJobCategories}
                            lastChangeSince={hrStore.evidenceLastChangeSince}
                            tableStore={tableStore}
                            openDeleteDialog={openDeleteDialog}
                            setOpenDeleteDialog={setOpenDeleteDialog}
                            openMoveDialog={openMoveDialog}
                            setOpenMoveDialog={setOpenMoveDialog}
                            setSomeSelected={setSomeSelected}
                        />
                    </div>
                    {tableStore.totalCount !== 0 && <tableStore.Pagination isMobile={isMobile} />}
                </div>
            </ContentWrapper>
        </div>
    );
});
